import { createRef, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import PageShell from '../../../components/layout/PageShell';
import { useAuth } from '../../../context/AuthContext';
import { fetchTournamentById, updateTournament } from '../../../services/tournamentService';
import { hasIndividualAccess } from '../../../utils/userRoles';
import { focusFirstError, normalizeFormError } from '../../../utils/formUx';
import './tournamentFormUx.css';

const IndividualTournamentEditPage = () => {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: '',
    tournamentLogo: '',
    tournamentBanner: '',
  });

  const hasAccess = useMemo(() => hasIndividualAccess(user), [user]);
  const fieldRefs = useMemo(() => ({ name: createRef(), tournamentLogo: createRef(), tournamentBanner: createRef() }), []);

  const setField = (key, value) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    if (errors[key]) setErrors((previous) => ({ ...previous, [key]: undefined }));
  };

  useEffect(() => {
    const loadTournament = async () => {
      if (!token || !tournamentId) return;
      setLoading(true);
      setStatus('');
      try {
        const response = await fetchTournamentById(tournamentId, token);
        const tournament = response?.tournament || response;
        setForm({
          name: tournament?.name || '',
          tournamentLogo: tournament?.tournamentLogo || '',
          tournamentBanner: tournament?.tournamentBanner || '',
        });
      } catch (error) {
        setStatus(error.message || 'Unable to load tournament.');
      } finally {
        setLoading(false);
      }
    };

    loadTournament();
  }, [token, tournamentId]);

  if (!token) return <Navigate to="/login" replace />;
  if (!hasAccess) return <Navigate to="/dashboard/individual/tournaments" replace />;

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!tournamentId) return;
    if (submitting) return;
    if (!form.name.trim()) {
      const nextErrors = { name: 'Enter the tournament name.' };
      setErrors(nextErrors);
      setStatus('1 field needs your attention.');
      focusFirstError(nextErrors, fieldRefs);
      return;
    }
    setSubmitting(true);
    setStatus('');

    try {
      await updateTournament(tournamentId, {
        name: form.name,
        tournamentLogo: form.tournamentLogo,
        tournamentBanner: form.tournamentBanner,
      }, token);
      setStatus('Tournament updated successfully.');
      navigate('/dashboard/individual/tournaments');
    } catch (error) {
      const normalized = normalizeFormError(error);
      setStatus(normalized.message);
      if (normalized.field && fieldRefs[normalized.field]) {
        const serverErrors = { ...errors, [normalized.field]: normalized.message };
        setErrors(serverErrors);
        focusFirstError(serverErrors, fieldRefs);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <div className="container py-4" style={{ maxWidth: 760 }}>
        <h1 className="h4 mb-3">Edit Tournament</h1>
        {loading ? <div className="alert alert-secondary">Loading tournament...</div> : null}
        {!loading ? (
          <form className="card p-3 d-grid gap-3" onSubmit={onSubmit} noValidate>
            <div>
              <label className="form-label" htmlFor="edit-tournament-name">Tournament Name</label>
              <input id="edit-tournament-name" ref={fieldRefs.name} aria-invalid={Boolean(errors.name)} aria-describedby="edit-tournament-name-error" className={`form-control ${errors.name ? 'is-invalid' : ''}`} value={form.name} onChange={(e) => setField('name', e.target.value)} />
              {errors.name ? <div id="edit-tournament-name-error" className="tournament-field-error" role="alert">{errors.name}</div> : null}
            </div>
            <div>
              <label className="form-label">Tournament Logo URL</label>
              <input ref={fieldRefs.tournamentLogo} className="form-control" value={form.tournamentLogo || ''} onChange={(e) => setField('tournamentLogo', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Tournament Banner URL</label>
              <input ref={fieldRefs.tournamentBanner} className="form-control" value={form.tournamentBanner || ''} onChange={(e) => setField('tournamentBanner', e.target.value)} />
            </div>
            <div className="d-flex gap-2">
              <button type="submit" disabled={submitting} className="btn btn-primary">{submitting ? 'Saving...' : 'Save changes'}</button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/dashboard/individual/tournaments')}>Cancel</button>
            </div>
          </form>
        ) : null}
        {status ? <div className="alert alert-info mt-3 mb-0">{status}</div> : null}
      </div>
    </PageShell>
  );
};

export default IndividualTournamentEditPage;
